import { Action, createAction, handleActions } from 'redux-actions'
import { call, cancel, put, select, takeLatest, takeLeading } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import { fetchAccountBasicInfo, fetchSimpleAccountList } from '@/api/AccountApi'
import * as Bridge from '@/api/BridgeApi'
import {
  checkIssuanceFee,
  DEBIT_CARD_API_URL,
  IDebitCardCheckIssuanceFeeResponse,
  IDebitCardIssueResponse,
} from '@/api/DebitCardApi'
import { ALERT_LAYER_TYPE } from '@/components/common/layer/Alert/AlertLayer'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import {
  EmptySimpleAccount,
  ISimpleAccountList,
  ISimpleAccountResponse,
} from '@/domains/AccountDomain'
import {
  DEBIT_CARD_ISSUE_ROUTES,
  DEBIT_CARD_ROUTES,
  ENTRY_ROUTE_QUERY,
  STATUS_CODE,
} from '@/domains/CommonDomain'
import { LOG_EVENT_NAME } from '@/domains/CommonDomain'
import {
  DebitCardStatusTypeCode,
  EmptyShippingAddress,
  IDebitCardInfo,
  IDebitCardType,
  ReIssueReasonTypeCode,
} from '@/domains/DebitCardDomain'
import { AddressType, addressTypeCode, IAddress } from '@/domains/ProfileDomain'
import { IRootState } from '@/modules'
import { SIMPLE_ACCOUNT, simpleAccountSelector } from '@/modules/account/SimpleAccountModule'
import { errorActions } from '@/modules/common/error'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import {
  debitCardIssueShippingActions,
  debitCardIssueShippingSelector,
} from '@/modules/debit-card/DebitCardIssueShippingModule'
import { profileSelector } from '@/modules/profile/ProfileModule'
import { createPostAction } from '@/utils/actionUtils'
import { BRIDGE_ERROR_CODE } from '@/utils/bridgeUtils/constants'
import {
  HostType,
  IRequestPinAuthResult,
  RequestPinAuthAction,
} from '@/utils/bridgeUtils/features/requestPinAuth'
import { newError } from '@/utils/errorUtils'
import { push } from '@/utils/historyUtils'
import { $ } from '@/utils/i18nUtils'
import { toInteger } from '@/utils/numberUtils'
import af from '@/utils/trackingUtils'
import { isUndefined } from '@/utils/validationUtils'

import { debitCardDetailActions, debitCardDetailSelector } from './DebitCardDetailModule'
import { debitCardIssueDesignSelector } from './DebitCardIssueDesignModule'

export const DEBIT_CARD_ISSUE = 'DebitCardIssue'

export interface IDebitCardIssueView {
  selectedDebitCardNo: string
  selectedCardType: string
  selectedAccount: ISimpleAccountResponse
  selectedAddress: AddressType
  issuanceFee: string
  defaultIssuanceFee: string
  linkedAccountNo: string
  errorCode?: number // check issuance fee 에러 팝업 예외처리
  errorMessage?: string // check issuance fee 에러 팝업 예외처리
}

export interface IDebitCardIssueMode {
  isEdit: boolean
  isReissue: boolean
  reIssueReasonType: ReIssueReasonTypeCode
  isTimeDepositReward: boolean
  isEZL: boolean
}

export interface IDebitCardIssueResult {
  card: IDebitCardType
  name: string
  account: { acctNo: string; title: string; balance: string }
  addressType: AddressType
  shippingAddress: IAddress
  issuanceFee: string
}

interface IDebitCardIssueState {
  view: IDebitCardIssueView
  mode: IDebitCardIssueMode
  temporaryView?: IDebitCardIssueView
}

const defaultDebitCardIssueView: IDebitCardIssueView = {
  selectedDebitCardNo: '',
  selectedCardType: 'design01',
  selectedAccount: EmptySimpleAccount,
  selectedAddress: AddressType.HOME,
  issuanceFee: '0',
  defaultIssuanceFee: '0',
  linkedAccountNo: '',
}

const defaultDebitCardIssueMode: IDebitCardIssueMode = {
  isEdit: false,
  isReissue: false,
  reIssueReasonType: ReIssueReasonTypeCode.LOST,
  isEZL: false,
  isTimeDepositReward: false,
}

const initialState: IDebitCardIssueState = {
  view: defaultDebitCardIssueView,
  mode: defaultDebitCardIssueMode,
}

const SET_VIEW = `${DEBIT_CARD_ISSUE}/setView`
const SET_MODE = `${DEBIT_CARD_ISSUE}/setMode`
const TO_EDIT = `${DEBIT_CARD_ISSUE}/toEdit`
const CANCEL_EDIT = `${DEBIT_CARD_ISSUE}/cancelEdit`

const SET_INITIAL_ACCOUNT = `${DEBIT_CARD_ISSUE}/setInitialAccount`
const SET_ACCOUNT = `${DEBIT_CARD_ISSUE}/setAccount`
export const CHECK_FEE = `${DEBIT_CARD_ISSUE}/checkFee`
const SET_NEW_ADDRESS = `${DEBIT_CARD_ISSUE}/setNewAddress`
const CONFIRM = `${DEBIT_CARD_ISSUE}/confirm`
const REISSUE = `${DEBIT_CARD_ISSUE}/reissue`

export const debitCardIssueActions = {
  setView: createAction<Partial<IDebitCardIssueView>>(SET_VIEW),
  setMode: createAction<Partial<IDebitCardIssueMode>>(SET_MODE),
  toEdit: createAction(TO_EDIT),
  cancelEdit: createAction(CANCEL_EDIT),
  setInitialAccount: createAction(SET_INITIAL_ACCOUNT),
  setAccount: createAction(SET_ACCOUNT),
  checkIssuanceFee: createAction(CHECK_FEE),
  setNewAddress: createAction(SET_NEW_ADDRESS),
  confirm: createAction(CONFIRM),
  reissue: createAction(REISSUE),
}
export const debitCardIssueAsync = createPostAction(DEBIT_CARD_ISSUE)

export const reducer = {
  [SET_VIEW]: (state: IDebitCardIssueState, action: Action<any>) => {
    return {
      ...state,
      view: {
        ...state.view,
        ...action.payload,
      },
    }
  },
  [SET_MODE]: (state: IDebitCardIssueState, action: Action<any>) => {
    return {
      ...state,
      mode: {
        ...state.mode,
        ...action.payload,
      },
    }
  },
  [TO_EDIT]: (state: IDebitCardIssueState) => {
    return {
      ...state,
      mode: {
        ...state.mode,
        isEdit: true,
      },
      temporaryView: state.view,
    }
  },
  [CANCEL_EDIT]: (state: IDebitCardIssueState) => {
    return {
      ...state,
      mode: {
        ...state.mode,
        isEdit: false,
      },
      view: state.temporaryView,
      temporaryView: undefined,
    }
  },
}

export const debitCardIssueSelector = {
  view: (state: IRootState) => oc(state[DEBIT_CARD_ISSUE]).view(defaultDebitCardIssueView),
  mode: (state: IRootState) => oc(state[DEBIT_CARD_ISSUE]).mode(defaultDebitCardIssueMode),
  isCorrectInputOfAccount: (state: IRootState) => {
    const selectedAccount = oc(state[DEBIT_CARD_ISSUE]).view.selectedAccount()
    const isReissue = oc(state[DEBIT_CARD_ISSUE]).mode.isReissue(false)
    const isEZL = oc(state[DEBIT_CARD_ISSUE]).mode.isEZL(false)
    const { issuanceFee } = debitCardIssueSelector.view(state)

    if (!selectedAccount || (!isReissue && !isEZL && selectedAccount.debitCardYn)) {
      return false
    }

    return toInteger(selectedAccount.baseCurJan) >= toInteger(issuanceFee)
  },

  isSelectableAccount: (state: IRootState) => {
    const isReissue = oc(state[DEBIT_CARD_ISSUE]).mode.isReissue(false)
    const simpleAccounts = oc(state[SIMPLE_ACCOUNT]).list([])

    return simpleAccounts.length > 1 && !isReissue
  },
  result: (state: IRootState): IDebitCardIssueResult => {
    const { selectedAccount, selectedAddress: addressType, issuanceFee } = oc(
      state[DEBIT_CARD_ISSUE],
    ).view(defaultDebitCardIssueView)
    const card = debitCardIssueDesignSelector.selectedCardType(state)
    const name = profileSelector.name(state)
    const account = {
      acctNo: selectedAccount.acctNo,
      title: selectedAccount.acctTitle,
      balance: selectedAccount.baseCurJan,
    }
    const shippingAddress = debitCardIssueShippingSelector.selectedAddress(state)

    return {
      card,
      name,
      account,
      addressType,
      shippingAddress,
      issuanceFee,
    }
  },
}

export const debitCardIssueReducer = handleActions(reducer, initialState)

export function* setInitialAccount() {
  try {
    const { isReissue, isEdit, isEZL } = yield select(debitCardIssueSelector.mode)

    /**
     *  ezl, fetch account list after Account Page entered not index(pentest)
     *  simpleAccountAsync.FETCH action dispatch work asynchronously,
     *  use `yield call` to use the response value immediately
     *  */
    let ezlInitialAccount
    if (isEZL) {
      const { list }: ISimpleAccountList = yield call(fetchSimpleAccountList)
      ezlInitialAccount = list.find(account => account.dpAcctYn)
    }

    if (isEdit) {
      return
    }

    yield put(loadingActions.start(DEBIT_CARD_ISSUE))

    /* 재발급, 바로 fee check */
    if (isReissue) {
      yield put(debitCardIssueActions.checkIssuanceFee())

      return
    }

    /* 신규발급, account 지정 후 fee check */
    const { linkedAccountNo } = yield select(debitCardIssueSelector.view)
    const accounts = yield select(simpleAccountSelector.simpleAccounts)
    const linkedAccount = accounts.find(
      (account: ISimpleAccountResponse) => account.acctNo === linkedAccountNo,
    )
    const availableAccount = accounts.find(
      (account: ISimpleAccountResponse) => !account.debitCardYn,
    )
    const initialAccount = isEZL ? ezlInitialAccount : linkedAccount || availableAccount

    if (!initialAccount) {
      yield put(layerActions.open(LEAVE_ALERT_TYPE.OPEN_ACCOUNT))
      yield put(
        debitCardIssueActions.setView({
          selectedAccount: accounts[0] ?? EmptySimpleAccount,
        }),
      )

      return
    }
    yield put(debitCardIssueActions.setView({ selectedAccount: initialAccount }))

    yield put(debitCardIssueActions.checkIssuanceFee()) // default account 에 해당하는 default fee

    if (accounts.length >= 2 && isUndefined(linkedAccount)) {
      yield put(debitCardIssueActions.setAccount())
    }
  } catch (e) {
    yield put(errorActions.set(e))
  } finally {
    yield put(loadingActions.finish(DEBIT_CARD_ISSUE))
  }
}

export function* setAccount() {
  try {
    const { selectedAccount: currentAccount } = yield select(debitCardIssueSelector.view)
    const { isEZL } = yield select(debitCardIssueSelector.mode)

    yield put(loadingActions.finish(CHECK_FEE)) // initial account fee check 으로 인한 로딩 중복 삭제

    const { result: selectedAccount } = yield call(Bridge.getWithdrawalAccount, {
      title: $('selectaccount_withdrawal_layermenu_title'),
      selectedAccountNo: currentAccount.acctNo,
      amount: '0',
    })

    if (!isEZL && selectedAccount.debitCardYn) {
      yield put(layerActions.open(LEAVE_ALERT_TYPE.NO_ADDITIONAL))

      return
    }

    if (!selectedAccount) {
      return
    }

    yield put(debitCardIssueActions.setView({ selectedAccount }))

    yield put(debitCardIssueActions.checkIssuanceFee())

    return
  } catch (e) {
    if (e.code === BRIDGE_ERROR_CODE.CLOSE) {
      yield cancel()
    }
    yield put(errorActions.set(e))
  }
}

export function* checkIssuanceFeeWithAccount() {
  try {
    yield put(loadingActions.start(CHECK_FEE))
    const { isReissue, isEZL } = yield select(debitCardIssueSelector.mode)

    if (isEZL) {
      // fee 체크 없이 1 고정
      yield put(debitCardIssueActions.setView({ issuanceFee: '1' }))

      return
    }

    const { selectedAccount, selectedDebitCardNo } = yield select(debitCardIssueSelector.view)
    const {
      card: { debitCardDesignTypeCode, debitCardBrand, debitCardVersion },
      shippingAddress: { cityCode, provinceCode },
    }: IDebitCardIssueResult = yield select(debitCardIssueSelector.result)
    const debitCardNo = isReissue ? selectedDebitCardNo : null

    const { issuanceFee, defaultIssuanceFee }: IDebitCardCheckIssuanceFeeResponse = yield call(
      checkIssuanceFee,
      {
        acctNo: selectedAccount.acctNo,
        debitCardNo,
        debitCardDesignTypeCode,
        debitCardBrand,
        debitCardVersion,
        cityCode,
        provinceCode,
      },
    )
    yield put(debitCardIssueActions.setView({ issuanceFee, defaultIssuanceFee }))
  } catch (e) {
    /* 카드발행 시, check issuance fee 실패해도 페이지에 남아있으면 다음 단계 진행 가능 -> WebView 를 닫아서 진행 막기 */
    yield put(
      debitCardIssueActions.setView({
        errorMessage: e.message,
        errorCode: e.code,
      }),
    )
    yield put(layerActions.open(LEAVE_ALERT_TYPE.DEBIT_CARD_ISSUE_HLI_ERROR))
  } finally {
    yield put(loadingActions.finish(CHECK_FEE))
  }
}

export function* setNewAddress() {
  try {
    const { result: newCustomAddress } = yield call(Bridge.getAddress, {
      checkAvailableDelivery: true,
    })

    if (!newCustomAddress) {
      return
    }
    console.warn('newAddress>>', newCustomAddress)
    yield put(debitCardIssueActions.setView({ selectedAddress: AddressType.ETC }))
    yield put(
      debitCardIssueShippingActions.setCustomAddress({
        ...newCustomAddress,
        isDisabledShipping: false,
      }),
    )
    yield put(layerActions.close(ALERT_LAYER_TYPE.NEW_ADDRESS))
  } catch (e) {
    switch (e.code) {
      case BRIDGE_ERROR_CODE.CLOSE:
        break
      case STATUS_CODE.CANNOT_DELIVERY:
        throw newError(STATUS_CODE.CANNOT_DELIVERY)
      default:
        yield put(errorActions.set(e))
        break
    }
  }
}

export function* confirm() {
  try {
    const isCorrectInputOfAccount = yield select(debitCardIssueSelector.isCorrectInputOfAccount)

    if (!isCorrectInputOfAccount) {
      yield put(layerActions.open(LEAVE_ALERT_TYPE.LOW_BALANCE))

      return
    }

    const { selectedDebitCardNo } = yield select(debitCardIssueSelector.view)
    const { isReissue, reIssueReasonType, isEZL } = yield select(debitCardIssueSelector.mode)
    const {
      account,
      issuanceFee,
      shippingAddress,
      card,
      addressType,
    }: IDebitCardIssueResult = yield select(debitCardIssueSelector.result)
    const { debitCardDesignTypeCode, debitCardBrand, debitCardVersion } = card
    const { isDisabledShipping, ...address } = shippingAddress

    const commonIssueRequest = {
      debitCardDesignTypeCode,
      debitCardBrand,
      debitCardVersion,
      issuanceFee,
      shippingAddress: { ...address, addressType: addressTypeCode?.[addressType] || 'H' },
    }
    // Adding json into request body such as: debitCardBrand & debitCardVersion
    const apiUrl = isReissue
      ? DEBIT_CARD_API_URL.RE_ISSUE_DEBIT_CARD
      : DEBIT_CARD_API_URL.NEW_ISSUE_DEBIT_CARD
    const request = isReissue
      ? {
          ...commonIssueRequest,
          reIssueReasonTypeCode: reIssueReasonType,
          debitCardNo: selectedDebitCardNo,
        }
      : { ...commonIssueRequest, acctNo: account.acctNo }

    const { result }: IRequestPinAuthResult = yield call(Bridge.enterTransactionPinForDebitCard, {
      hostType: HostType.BANK_API,
      apiUrl,
      request,
    })

    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (result.action === RequestPinAuthAction.CLOSE) {
      return
    }
    if (
      result.action === RequestPinAuthAction.AUTH_FAIL ||
      result.action === RequestPinAuthAction.AUTH_ERROR
    ) {
      // const { code } = JSON.parse(result.response)
      // throw newError(code)
      throw JSON.parse(result.response)
    }

    if (result.action === RequestPinAuthAction.AUTH_SUCCESS) {
      const response = JSON.parse(result.response)
      const { debitCardNo, acctNo }: IDebitCardIssueResponse = response

      if (!response || !debitCardNo || (!isEZL && !acctNo)) {
        throw newError(STATUS_CODE.INVALID_RESPONSE_ERROR)
      }

      yield put(debitCardDetailActions.changeDebitCardIdentity({ debitCardNo, acctNo }))
      yield put(debitCardIssueShippingActions.setCustomAddress(EmptyShippingAddress))
      if (isEZL) {
        yield call(push, `${DEBIT_CARD_ROUTES.DETAIL}?${ENTRY_ROUTE_QUERY}`)
      } else {
        yield call(
          push,
          `${DEBIT_CARD_ROUTES.DETAIL}/${debitCardNo}/${acctNo}?${ENTRY_ROUTE_QUERY}`,
        )
      }
      yield call(af.send, LOG_EVENT_NAME.APP_DEBITCARD_APPLICATION_SUCCESS)

      return
    }

    throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
  } catch (e) {
    yield put(errorActions.set(e))
  }
}

export function* reissue() {
  try {
    const debitCardInfo: IDebitCardInfo = yield select(debitCardDetailSelector.debitCardInfo)

    if (!debitCardInfo) {
      return
    }
    const acctNo = debitCardInfo?.additionalInfo?.linkedAcctNo

    if (!acctNo) {
      return
    }
    const isLost = debitCardInfo.debitCardStatusTypeCode === DebitCardStatusTypeCode.LOST
    const reIssueReasonType = isLost ? ReIssueReasonTypeCode.LOST : ReIssueReasonTypeCode.DAMAGED

    const {
      acctColor,
      acctTitle,
      acctCcy,
      dpAcctYn,
      payYn,
      subjCd,
      bsJan,
    } = yield call(fetchAccountBasicInfo, { acctNo })
    const selectedAccount: ISimpleAccountResponse = {
      acctNo,
      acctColor,
      acctTitle,
      baseAcctCcy: acctCcy,
      baseCurJan: bsJan,
      debitCardYn: true,
      dpAcctYn,
      payYn,
      subjCd,

      // billpayment 에서 거래시 loan 기능 사용으로 인해 추가함
      loanLinkYn: false, // 대출 가능 여부
      creditJan: '0', // 대출가능잔액
      lonAcctNo: '0', // 대출계좌번호
    }

    yield put(debitCardIssueActions.setMode({ isReissue: true, reIssueReasonType }))
    yield put(
      debitCardIssueActions.setView({
        selectedDebitCardNo: debitCardInfo.debitCardNo,
        selectedCardType: debitCardInfo.debitCardDesignTypeCode,
        // selectedCardType: debitCardInfo.debitCardDesignTypeCode && debitCardInfo.debitCardBrand && debitCardInfo.debitCardVersion,
        selectedAccount,
      }),
    )

    yield call(push, DEBIT_CARD_ISSUE_ROUTES.DESIGN)
  } catch (e) {
    yield put(errorActions.set(e))
  }
}

export const debitCardIssueSaga = [
  takeLatest(SET_ACCOUNT, setAccount),
  takeLatest(SET_NEW_ADDRESS, setNewAddress),
  takeLeading(CONFIRM, confirm),
  takeLatest(SET_INITIAL_ACCOUNT, setInitialAccount),
  takeLatest(REISSUE, reissue),
  takeLatest(CHECK_FEE, checkIssuanceFeeWithAccount),
]
